import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, InputNumber, Switch, Upload, message, Image, Tag } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined, UploadOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import apiClient from '../services/apiConfig';

const BASE_IMAGE_URL = 'https://chiavari.sbcode.com.ar/api/public/storage/rewards/';

interface Reward {
  id: number;
  name: string;
  description: string;
  points_cost: number;
  image: string | null;
  is_active: boolean;
}

const RewardsPage: React.FC = () => {
  const [rewards, setRewards] = useState<Reward[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedReward, setSelectedReward] = useState<Reward | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchRewards();
  }, []);

  const fetchRewards = async () => {
    try {
      const response = await apiClient.get('/rewards');
      if (response.data.success) {
        setRewards(response.data.data);
      }
    } catch (error) {
      message.error('Error al cargar premios');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setSelectedReward(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (reward: Reward) => {
    setSelectedReward(reward);
    form.setFieldsValue({
      name: reward.name,
      description: reward.description,
      points_cost: reward.points_cost,
      is_active: reward.is_active,
      image: reward.image ? [{
        uid: '-1',
        name: reward.image,
        status: 'done',
        url: `${BASE_IMAGE_URL}${reward.image}`
      }] : []
    });
    setIsModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    Modal.confirm({
      title: '¿Eliminar este premio?',
      icon: <ExclamationCircleOutlined />,
      content: 'Esta acción no se puede deshacer',
      okText: 'Eliminar',
      okType: 'danger',
      cancelText: 'Cancelar',
      async onOk() {
        try {
          await apiClient.delete(`/rewards/${id}`);
          message.success('Premio eliminado');
          fetchRewards();
        } catch (error) {
          message.error('Error al eliminar premio');
        }
      }
    });
  };

  const handleSubmit = async (values: any) => {
    try {
      const formData = new FormData();
      formData.append('name', values.name);
      formData.append('description', values.description || '');
      formData.append('points_cost', values.points_cost.toString());
      formData.append('is_active', values.is_active ? '1' : '0');

      if (values.image && values.image[0]?.originFileObj) {
        formData.append('image', values.image[0].originFileObj);
      }

      if (selectedReward) {
        await apiClient.post(`/rewards/${selectedReward.id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        message.success('Premio actualizado');
      } else {
        await apiClient.post('/rewards', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        message.success('Premio creado');
      }

      setIsModalVisible(false);
      form.resetFields();
      fetchRewards();
    } catch (error) {
      message.error('Error al guardar premio');
    }
  };

  const columns = [
    {
      title: 'Imagen',
      dataIndex: 'image',
      key: 'image',
      width: 100,
      render: (image: string | null) => image ? (
        <Image src={`${BASE_IMAGE_URL}${image}`} alt="premio" width={60} height={60} style={{ objectFit: 'cover', borderRadius: 4 }} />
      ) : <div style={{ width: 60, height: 60, background: '#f0f0f0', borderRadius: 4 }} />
    },
    {
      title: 'Nombre',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Descripción',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: 'Puntos',
      dataIndex: 'points_cost',
      key: 'points_cost',
      render: (points: number) => <Tag color="blue">{points} pts</Tag>
    },
    {
      title: 'Estado',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (active: boolean) => (
        <Tag color={active ? 'green' : 'red'}>{active ? 'Activo' : 'Inactivo'}</Tag>
      )
    },
    {
      title: 'Acciones',
      key: 'actions',
      render: (_: any, record: Reward) => (
        <>
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)}>Editar</Button>
          <Button type="link" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)}>Eliminar</Button>
        </>
      )
    }
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24 }}>Premios Internos</h1>
          <p style={{ margin: '4px 0 0 0', color: '#666' }}>Gestiona los premios que los huéspedes pueden canjear con puntos</p>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          Nuevo Premio
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={rewards}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 10 }}
      />

      <Modal
        title={selectedReward ? 'Editar Premio' : 'Crear Premio'}
        open={isModalVisible}
        onCancel={() => { setIsModalVisible(false); form.resetFields(); }}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit} initialValues={{ is_active: true }}>
          <Form.Item name="name" label="Nombre" rules={[{ required: true, message: 'Ingresa el nombre' }]}>
            <Input />
          </Form.Item>

          <Form.Item name="description" label="Descripción">
            <Input.TextArea rows={3} />
          </Form.Item>

          <Form.Item name="points_cost" label="Costo en puntos" rules={[{ required: true, message: 'Ingresa el costo' }]}>
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="image" label="Imagen" valuePropName="fileList" getValueFromEvent={(e) => Array.isArray(e) ? e : e?.fileList}>
            <Upload listType="picture" maxCount={1} beforeUpload={() => false}>
              <Button icon={<UploadOutlined />}>Seleccionar imagen</Button>
            </Upload>
          </Form.Item>

          <Form.Item name="is_active" label="Activo" valuePropName="checked">
            <Switch />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" style={{ marginRight: 8 }}>
              {selectedReward ? 'Actualizar' : 'Crear'}
            </Button>
            <Button onClick={() => { setIsModalVisible(false); form.resetFields(); }}>
              Cancelar
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default RewardsPage;
